import { isWebDataSuccess } from '@cobliteam/api-lib';
import type { TimestampInMillis } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { selectAddress } from '@cobliteam/store';
import type React from 'react';
import { useSelector } from 'react-redux';
import {
  TrackingPopUp,
  TrackingPopUpContent,
  TrackingPopUpFooter,
  TrackingPopUpHeader,
  TrackingPopUpHeaderMain,
  TrackingPopUpInfoRowText,
  TrackingPopUpSkeleton,
  TrackingPopUpTitle,
} from '../Tracking/components/Common/TrackingPopUp';
import { EndIconComponent, StartIconComponent } from '../Tracking/components/Map';
import { formatTimestamp } from '../Tracking/utils/date';
import OngoingRouteBadge from './OngoingRouteBadge';

export interface StartEndPopupProps {
  times: {
    start?: TimestampInMillis;
    end?: TimestampInMillis;
  };
  isTrackingToday?: boolean;
  driverName?: string;
  children?: React.ReactNode;
}

export const StartEndPopup = ({ times, driverName, isTrackingToday, children }: StartEndPopupProps) => {
  const { t } = useTranslation('tracking');
  const addressWebData = useSelector(selectAddress);
  const { start, end } = times;

  const address = isWebDataSuccess(addressWebData) ? addressWebData.data : <TrackingPopUpSkeleton />;

  const getContent = () => {
    if (end && isTrackingToday) {
      return {
        title: t('ongoing_route'),
      };
    }

    return end ? { title: t('route_end'), icon: <EndIconComponent /> } : { title: t('route_start'), icon: <StartIconComponent /> };
  };

  const content = getContent();

  return (
    <TrackingPopUp>
      <TrackingPopUpHeader>
        <TrackingPopUpHeaderMain>
          {content.icon}
          <TrackingPopUpTitle>{content.title}</TrackingPopUpTitle>
        </TrackingPopUpHeaderMain>
        {isTrackingToday && <OngoingRouteBadge />}
      </TrackingPopUpHeader>

      <TrackingPopUpContent>
        {start && <TrackingPopUpInfoRowText title={t('start')}>{formatTimestamp(start)}</TrackingPopUpInfoRowText>}
        {end && <TrackingPopUpInfoRowText title={t('last_update')}>{formatTimestamp(end)}</TrackingPopUpInfoRowText>}
        <TrackingPopUpInfoRowText title={t('address')}>{address}</TrackingPopUpInfoRowText>
        <TrackingPopUpInfoRowText title={t('driver')}>{driverName || t('no_driver')}</TrackingPopUpInfoRowText>
      </TrackingPopUpContent>

      <TrackingPopUpFooter>{children}</TrackingPopUpFooter>
    </TrackingPopUp>
  );
};
